/**
 * Agente TA Metrics — normalización de la "TA Metrics - Base agente" y catálogo
 * de métricas. Módulo PURO (sin I/O, sin fechas implícitas): port fiel de la
 * sección 7 del documento técnico de Ignacio Bresso (11/09/2026), que a su vez
 * es la sección 3 de las instrucciones del Proyecto de Claude.
 *
 * Reglas congeladas: estados, fechas, períodos y métricas solo cambian si el
 * testing muestra un error funcional concreto y lo aprueba TA. Lo que sí es
 * configuración (RNF-08 / RF-19): ETAPAS_ABIERTAS, MAPEO_OWNERS y, desde la
 * V1.3 (doc v5, 18/09/2026), los diccionarios de seniority y de términos de
 * negocio. La V1.3 suma el embudo de candidatos (8 columnas nuevas de la Base
 * agente: 7 Ready to Offer … 2 TA Interview, 1 Contactado, Hired); se lee por
 * NOMBRE de columna, así el corrimiento de Hire date (S → AA) no afecta.
 *
 * Lo consumen el dashboard y las herramientas del bot: un solo DataFrame
 * normalizado para todo, así las métricas fijas y el análisis parten de lo mismo.
 */
import { parseCsvRows } from './csv';
import { todayAR, daysBetweenISO } from './dates';
import { foldKey } from './text-normalize';
import { mailsDelLead } from './ta-hiring-manager';

// ─── Configuración (RNF-08) ──────────────────────────────────────────────────

export const ETAPAS_ABIERTAS = [
  '1 Hunting',
  '2 TA Interview',
  '3 HM Interview',
  '4 Prueba Técnica',
  '5 Bar Raiser Interview',
  '6 Existential Interview',
  '7 Job Offer',
] as const;

export const MAPEO_OWNERS: Record<string, string> = {
  'Luis/Maflo': 'Maflo',
  'Maflo/Dani': 'Dani',
  'Ari/Flor': 'Ari',
  'Luis/Ari': 'Luis',
  'Luis/Flor': 'Luis',
};

/**
 * Seniority canónico (doc v5 §14.2): la escala se renombró en 2024 y el mismo
 * nivel quedó cargado con etiquetas distintas según el año. Nivel → valores de
 * la planilla que agrupa. Los valores que no figuran acá se usan tal cual
 * (Trainee, Junior, Coordinator, Lead, Head, Director, Strategist…). Manager y
 * Sr Manager son roles distintos y NO se agrupan.
 */
export const SENIORITY_NIVELES: Record<string, string[]> = {
  'Sr Analyst': ['Sr Analyst', 'Senior', 'Semi Senior', 'Semi senior / Senior'],
  Analyst: ['Analyst'],
  'Sr Manager': ['Sr Manager', 'Senior Manager'],
  Manager: ['Manager'],
};

/** Valores de Seniority ambiguos: quedan fuera de los cortes por nivel (se informan si son materiales). */
export const SENIORITY_AMBIGUOS = ['Analyst / Semi Senior', 'Sr/Coordinator'];

/**
 * Términos de negocio (doc v5 §14.3): lo que escribe la gente → valor real de
 * la planilla. Se busca en Area, Team, Position y Position Information. Solo
 * equivalencias confirmadas por TA; el bot no inventa sinónimos.
 */
export const TERMINOS_NEGOCIO: Record<string, string> = {
  'paid media': 'Performance',
  'performance marketing': 'Performance',
  'media buying': 'Performance',
  pauta: 'Performance',
};

export interface TaMetricsConfig {
  etapasAbiertas: readonly string[];
  mapeoOwners: Record<string, string>;
  /** Nivel canónico → valores de la planilla que agrupa (RF-19). */
  seniorityNiveles: Record<string, string[]>;
  /** Término del usuario → valor de la planilla (RF-19). */
  terminosNegocio: Record<string, string>;
}

export const DEFAULT_TA_CONFIG: TaMetricsConfig = {
  etapasAbiertas: ETAPAS_ABIERTAS,
  mapeoOwners: MAPEO_OWNERS,
  seniorityNiveles: SENIORITY_NIVELES,
  terminosNegocio: TERMINOS_NEGOCIO,
};

// ─── Candidatos por búsqueda (V1.3, §6 y §7 "Parser de candidatos") ──────────

/** Columna del embudo → nivel (2 a 7). Los encabezados van tal cual la Hiring Plan (sin tilde en Tecnica). */
export const ETAPAS_CANDIDATOS: Record<string, number> = {
  '2 TA Interview': 2,
  '3 HM Interview': 3,
  '4 Prueba Tecnica': 4,
  '5 Bar Raiser': 5,
  '6 Existential': 6,
  '7 Ready to Offer': 7,
};
export const COLUMNAS_EMBUDO = [...Object.keys(ETAPAS_CANDIDATOS), '1 Contactado', 'Hired'] as const;

export type TaOrigen = 'Hunting' | 'Referido' | 'Interno' | 'Sin informar';
const ORIGENES: Record<string, TaOrigen> = { H: 'Hunting', R: 'Referido', I: 'Interno' };
/** Textos que no son un nombre de candidato. */
const NO_CANDIDATO = new Set(['', '-', 'nan', 'validar', 'tbd', 'n/a']);

export interface TaCandidato {
  etapa: string;
  nivel: number;
  nombre: string;
  origen: TaOrigen;
}

/**
 * "I: LUIS CARDENAS // H: Mariana Ramos, Pedro Gil" → [{Luis, Interno}, {Mariana, Hunting}, {Pedro, Hunting}].
 * Tolerante: bloques sin prefijo quedan "Sin informar"; separadores "//" entre
 * orígenes y "," o "/" entre nombres; celdas que no son nombres ("validar") no cuentan.
 */
export function parsearCandidatos(celda: string | null | undefined): Array<{ nombre: string; origen: TaOrigen }> {
  const txt = String(celda ?? '').trim();
  if (NO_CANDIDATO.has(txt.toLowerCase())) return [];
  const out: Array<{ nombre: string; origen: TaOrigen }> = [];
  for (const bloque of txt.split('//')) {
    const b = bloque.trim();
    if (!b) continue;
    const m = /^([HRI])\s*:\s*(.*)$/i.exec(b);
    const origen: TaOrigen = m ? ORIGENES[m[1].toUpperCase()] : 'Sin informar';
    const resto = m ? m[2] : b;
    for (const raw of resto.split(/[,/]/)) {
      const nombre = raw.trim();
      if (nombre && !NO_CANDIDATO.has(nombre.toLowerCase())) out.push({ nombre, origen });
    }
  }
  return out;
}

export type TaEstado =
  | 'Abierta'
  | 'Cerrada'
  | 'Cancelada'
  | 'Stand By'
  | 'Request'
  | 'Pipeline'
  | 'Programada'
  | 'Otro';

export const ESTADOS_NO_ABIERTOS: TaEstado[] = ['Request', 'Pipeline', 'Programada', 'Stand By', 'Cancelada'];

/** Columnas originales del CSV (todas texto, ya trimmed) + derivadas. */
export interface TaRow {
  // originales
  ID: string;
  Date: string;
  Area: string;
  Team: string;
  Reason: string;
  Position: string;
  'Position Information': string;
  Seniority: string;
  Site: string;
  'Expectativa de Cierre': string;
  'Industry / Non industry': string;
  'Interno/Externo': string;
  Priority: string;
  Lead: string;
  /** Columna nueva (TA, 30/09/2026). Puede traer VARIOS mails en una celda. */
  'Lead Mail': string;
  'Inhouse / Outsource': string;
  Owner: string;
  Partner: string;
  OL: string;
  'Hire date': string;
  'Starting date': string;
  'Time to cover': string;
  'Activo/Inactivo': string;
  // embudo de candidatos (V1.3; vacías si la base no trae las columnas)
  '7 Ready to Offer': string;
  '6 Existential': string;
  '5 Bar Raiser': string;
  '4 Prueba Tecnica': string;
  '3 HM Interview': string;
  '2 TA Interview': string;
  '1 Contactado': string;
  Hired: string;
  // derivadas
  /**
   * Los mails de `Lead Mail`, normalizados y separados: una celda puede nombrar a
   * varios Hiring Managers y la búsqueda es de todos. Vacío si la fila no lo trae.
   */
  lead_mails: string[];
  /** 'YYYY-MM-DD' o null (Date con texto de estado queda vacía). */
  fecha_apertura: string | null;
  fecha_cierre: string | null;
  fecha_ingreso: string | null;
  estado: TaEstado;
  /** Solo Cerradas con ambas fechas; incluye 0 y negativos. */
  dias_cierre: number | null;
  recruiter: string;
  pais: string;
  tipo: string;
  /** Nivel canónico de Seniority (Sr Analyst ≡ Senior ≡ Semi Senior…); el valor tal cual si no está en el diccionario; null si es ambiguo. */
  seniority_nivel: string | null;
  /** Candidatos que están HOY en cada etapa 2–7 (foto, no histórico). */
  candidatos: TaCandidato[];
  /** Cantidad de nombres en etapas 2 a 7. */
  candidatos_en_proceso: number;
  /** Proxy de entrevistados por el Hiring Manager: candidatos en etapas 3 a 7. */
  candidatos_post_hm: number;
  /** "1 Contactado" como número; null si no es un número. NUNCA se suma al embudo. */
  contactados: number | null;
  /** "Hired" limpio. */
  contratado: string;
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

/** Parser RFC 4180 (coma fija): desde el 27/10/2026 es el compartido de shared/csv.ts (celdas recortadas, filas vacías fuera). */
export function parseCsv(text: string): string[][] {
  return parseCsvRows(text, ',');
}

// ─── Fechas (sin zona horaria: todo es 'YYYY-MM-DD') ─────────────────────────

const RE_ISO = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const RE_DMY = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

function validYmd(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** aaaa-mm-dd, d/m/aaaa o d-m-aaaa (como el `_fecha` de pandas). Lo que no es fecha ⇒ null. */
export function parseFecha(raw: string | undefined | null): string | null {
  const s = (raw ?? '').trim();
  if (!s) return null;
  let m = RE_ISO.exec(s);
  if (m) return validYmd(+m[1], +m[2], +m[3]);
  m = RE_DMY.exec(s);
  if (m) return validYmd(+m[3], +m[2], +m[1]);
  return null;
}

/** Días entre dos 'YYYY-MM-DD' (b − a). Vive en shared/dates. */
export const diffDays = daysBetweenISO;

/** "Hoy" del negocio: fecha en America/Argentina/Buenos_Aires como 'YYYY-MM-DD' (RNF-02). */
export function hoyBuenosAires(now: Date = new Date()): string {
  return todayAR(now);
}

// ─── Normalización (sección 7 / Anexo A §3) ──────────────────────────────────

/**
 * Devuelve las filas normalizadas. `hoy` es inyectable (tests y snapshots).
 * Orden de asignación de estado: cada paso pisa al anterior.
 */
export function normalizar(csv: string, hoy: string, cfg: TaMetricsConfig = DEFAULT_TA_CONFIG): TaRow[] {
  const table = parseCsv(csv);
  if (table.length < 2) return [];
  const headers = table[0].map((h) => h.trim());
  const idx = new Map(headers.map((h, i) => [h, i] as const));
  const col = (row: string[], name: string) => (row[idx.get(name) ?? -1] ?? '').trim();
  const etapas = new Set(cfg.etapasAbiertas);
  const nivelDe = seniorityResolver(cfg);
  const out: TaRow[] = [];

  for (const raw of table.slice(1)) {
    const ID = col(raw, 'ID');
    if (!ID) continue; // df[df["ID"].notna() & (df["ID"] != "")]

    const Date_ = col(raw, 'Date');
    const OL = col(raw, 'OL');
    const leadMailRaw = col(raw, 'Lead Mail');
    const fecha_apertura = parseFecha(Date_);
    const fecha_cierre = parseFecha(col(raw, 'Hire date'));
    const fecha_ingreso = parseFecha(col(raw, 'Starting date'));

    let estado: TaEstado = 'Otro';
    if (OL === '8 Covered') estado = 'Cerrada';
    if (OL === '9 Cancelled') estado = 'Cancelada';
    if (OL === '0 Stand By') estado = 'Stand By';
    if (etapas.has(OL)) estado = 'Abierta';
    // En etapas 1 a 7, el texto de estado en Date prevalece sobre OL
    const textoDate = Date_.toLowerCase();
    if (etapas.has(OL)) {
      if (textoDate === 'on hold') estado = 'Stand By';
      if (textoDate === 'cancelled' || textoDate === 'canceled' || textoDate === 'cancelada') estado = 'Cancelada';
      if (textoDate === 'request') estado = 'Request';
      if (textoDate === 'pipeline') estado = 'Pipeline';
    }
    // Abiertas con fecha futura: todavía no abrieron oficialmente
    if (estado === 'Abierta' && fecha_apertura && fecha_apertura > hoy) estado = 'Programada';

    const dias_cierre =
      estado === 'Cerrada' && fecha_apertura && fecha_cierre ? diffDays(fecha_apertura, fecha_cierre) : null;

    // Recruiter: Owner sin espacios alrededor de "/", con el mapeo de compartidos
    const ownerRaw = col(raw, 'Owner');
    const owner = (ownerRaw || 'Sin owner').replace(/\s*\/\s*/g, '/');
    const recruiter = cfg.mapeoOwners[owner] ?? owner;

    const pais = col(raw, 'Site') || 'Sin país';
    const ie = col(raw, 'Interno/Externo');
    const tipo = !ie || ie === 'TBD' ? 'Sin definir' : ie;

    // Embudo (V1.3): una entrada por nombre, con la etapa y el origen
    const candidatos: TaCandidato[] = [];
    for (const [etapa, nivel] of Object.entries(ETAPAS_CANDIDATOS)) {
      for (const c of parsearCandidatos(col(raw, etapa))) candidatos.push({ etapa, nivel, ...c });
    }
    const contactadosRaw = col(raw, '1 Contactado').replace(',', '.');
    const contactados = contactadosRaw !== '' && Number.isFinite(Number(contactadosRaw)) ? Number(contactadosRaw) : null;

    out.push({
      ID,
      Date: Date_,
      Area: col(raw, 'Area'),
      Team: col(raw, 'Team'),
      Reason: col(raw, 'Reason'),
      Position: col(raw, 'Position'),
      'Position Information': col(raw, 'Position Information'),
      Seniority: col(raw, 'Seniority'),
      Site: col(raw, 'Site'),
      'Expectativa de Cierre': col(raw, 'Expectativa de Cierre'),
      'Industry / Non industry': col(raw, 'Industry / Non industry'),
      'Interno/Externo': ie,
      Priority: col(raw, 'Priority'),
      Lead: col(raw, 'Lead'),
      'Lead Mail': leadMailRaw,
      'Inhouse / Outsource': col(raw, 'Inhouse / Outsource'),
      Owner: ownerRaw,
      Partner: col(raw, 'Partner'),
      OL,
      'Hire date': col(raw, 'Hire date'),
      'Starting date': col(raw, 'Starting date'),
      'Time to cover': col(raw, 'Time to cover'),
      'Activo/Inactivo': col(raw, 'Activo/Inactivo'),
      '7 Ready to Offer': col(raw, '7 Ready to Offer'),
      '6 Existential': col(raw, '6 Existential'),
      '5 Bar Raiser': col(raw, '5 Bar Raiser'),
      '4 Prueba Tecnica': col(raw, '4 Prueba Tecnica'),
      '3 HM Interview': col(raw, '3 HM Interview'),
      '2 TA Interview': col(raw, '2 TA Interview'),
      '1 Contactado': col(raw, '1 Contactado'),
      Hired: col(raw, 'Hired'),
      lead_mails: mailsDelLead(leadMailRaw),
      fecha_apertura,
      fecha_cierre,
      fecha_ingreso,
      estado,
      dias_cierre,
      recruiter,
      pais,
      tipo,
      seniority_nivel: nivelDe(col(raw, 'Seniority')),
      candidatos,
      candidatos_en_proceso: candidatos.length,
      candidatos_post_hm: candidatos.filter((c) => c.nivel >= 3).length,
      contactados,
      contratado: col(raw, 'Hired'),
    });
  }
  return out;
}

/** ¿El CSV trae las columnas del embudo? (la base vieja de 22 columnas no) */
export function tieneEmbudo(csv: string): boolean {
  const first = csv.split(/\r?\n/, 1)[0] ?? '';
  const headers = new Set(parseCsv(first)[0]?.map((h) => h.trim()) ?? []);
  return COLUMNAS_EMBUDO.every((c) => headers.has(c));
}

// ─── Vocabulario (V1.3, §14) ────────────────────────────────────────────────

/**
 * Resuelve un valor de Seniority (de la planilla o escrito por la persona) a su
 * nivel canónico según el diccionario. Devuelve null para los ambiguos y el
 * valor tal cual (recortado) si no está en el diccionario.
 */
export function seniorityResolver(cfg: TaMetricsConfig = DEFAULT_TA_CONFIG): (valor: string) => string | null {
  const porCanon = new Map<string, string>();
  for (const [nivel, valores] of Object.entries(cfg.seniorityNiveles)) {
    porCanon.set(canon(nivel), nivel);
    for (const v of valores) porCanon.set(canon(v), nivel);
  }
  const ambiguos = new Set(SENIORITY_AMBIGUOS.map(canon));
  return (valor: string) => {
    const v = String(valor ?? '').trim();
    if (!v) return '';
    const c = canon(v);
    if (ambiguos.has(c)) return null;
    return porCanon.get(c) ?? v;
  };
}

export const CAMPOS_TERMINO = ['Area', 'Team', 'Position', 'Position Information'] as const;
export type CampoTermino = (typeof CAMPOS_TERMINO)[number];

/**
 * Un término de negocio ("paid media", "studio", "content partner") buscado en
 * Area, Team, Position y Position Information (§14.3): resuelve el diccionario,
 * compara por forma canónica y por contención de tokens, y dice en qué campo
 * matcheó. Si matchea en varios niveles gana el más amplio (Area > Team > …).
 */
export function resolverTermino<T extends TaFiltrable>(rows: T[], termino: string, cfg: TaMetricsConfig = DEFAULT_TA_CONFIG): { termino: string; buscado: string; campo: CampoTermino | null; valores: string[]; filas: T[] } {
  const pedido = String(termino ?? '').trim();
  const dic = Object.entries(cfg.terminosNegocio).find(([k]) => canon(k) === canon(pedido));
  const buscado = dic ? dic[1] : pedido;
  const want = canon(buscado);
  const wantTokens = want.split(' ').filter(Boolean);
  const matchea = (v: string) => {
    const c = canon(v);
    if (!c) return false;
    if (c === want) return true;
    const tokens = new Set(c.split(' '));
    return wantTokens.length > 0 && wantTokens.every((t) => tokens.has(t));
  };
  for (const campo of CAMPOS_TERMINO) {
    const filas = rows.filter((r) => matchea(String(r[campo] ?? '')));
    if (filas.length) {
      const valores = [...new Set(filas.map((r) => String(r[campo]).trim()))].sort();
      return { termino: pedido, buscado, campo, valores, filas };
    }
  }
  return { termino: pedido, buscado, campo: null, valores: [], filas: [] };
}

// ─── Catálogo de métricas (sección 7) ────────────────────────────────────────

/** Dimensiones de corte permitidas (lo que el bot y el dashboard pueden agrupar). */
export const DIMENSIONES = [
  'recruiter', 'pais', 'tipo', 'estado', 'Area', 'Team', 'Position', 'Position Information',
  'Seniority', 'Reason', 'Lead', 'Inhouse / Outsource', 'Partner', 'OL', 'Priority', 'Industry / Non industry',
] as const;
export type TaDimension = (typeof DIMENSIONES)[number];

/** Un valor o varios (cualquiera de ellos): la vista Hiring Plan filtra por varios Teams/recruiters a la vez (§15, RD-02). */
export type TaFiltroTexto = string | string[];

export interface TaFiltros {
  estado?: TaEstado | TaEstado[];
  recruiter?: TaFiltroTexto;
  pais?: TaFiltroTexto;
  tipo?: TaFiltroTexto;
  Area?: TaFiltroTexto;
  Team?: TaFiltroTexto;
  Position?: TaFiltroTexto;
  Lead?: TaFiltroTexto;
  /** Se compara por NIVEL canónico: "senior" trae Sr Analyst + Senior + Semi Senior (doc v5 §14.2). */
  Seniority?: TaFiltroTexto;
  /** Término de negocio buscado en Area/Team/Position/Position Information (§14.3). */
  termino?: string;
}

const norm = (s: string) => foldKey(s);
const eq = (a: string, b: string) => norm(a) === norm(b);

/**
 * Sinónimos y abreviaturas que la gente escribe distinto de como está cargado
 * (Ignacio 17/09: "Senior Analyst", "Sr. Analyst", "senior analists" ⇒ "Sr Analyst").
 * Token → token canónico, después de quitar puntuación y pasar a minúsculas.
 */
const TOKEN_ALIASES: Record<string, string> = {
  senior: 'sr', seniors: 'sr',
  junior: 'jr', juniors: 'jr',
  semisenior: 'ssr', semisr: 'ssr',
  analista: 'analyst', analistas: 'analyst', analysts: 'analyst', analists: 'analyst', analist: 'analyst',
  leads: 'lead', lider: 'lead', lideres: 'lead',
  managers: 'manager', gerente: 'manager',
  strategists: 'strategist', estratega: 'strategist',
  specialists: 'specialist', especialista: 'specialist',
  directors: 'director', directores: 'director',
  heads: 'head',
  partners: 'partner',
  interno: 'interno', internas: 'interno', interna: 'interno', internos: 'interno',
  externo: 'externo', externas: 'externo', externa: 'externo', externos: 'externo',
};

/**
 * Forma canónica de un valor de texto para comparar: sin tildes ni puntuación,
 * minúsculas, sinónimos resueltos y tokens ordenados ("Analyst Sr" ≡ "Sr. Analyst").
 * Puro: lo usan los filtros del bot y las sugerencias de valores cercanos.
 */
export function canon(s: string): string {
  const base = norm(s)
    .replace(/semi[\s-]?senior/g, 'ssr')
    .replace(/[^a-z0-9/+ ]+/g, ' ');
  const tokens = base
    .split(/[\s/]+/)
    .filter(Boolean)
    .map((t) => TOKEN_ALIASES[t] ?? t);
  return [...new Set(tokens)].sort().join(' ');
}
const same = (a: string, b: string) => canon(a) === canon(b);

/** Campos de texto libre que se filtran por forma canónica. */
export const CAMPOS_FILTRO_TEXTO = ['recruiter', 'pais', 'tipo', 'Area', 'Team', 'Position', 'Lead', 'Seniority'] as const;

/**
 * Filtro tolerante: `estado` acepta uno o varios (acentos/case); los campos de
 * texto comparan por forma canónica (ver `canon`): "senior analyst" encuentra
 * "Sr Analyst", "mexico" encuentra "México", "Analyst" NO encuentra "Sr Analyst".
 */
/** Lo mínimo que necesita `filtrar`: lo tienen las filas vivas (TaRow) y, más adelante, las fotos del histórico. */
export type TaFiltrable = Pick<TaRow, 'estado' | 'recruiter' | 'pais' | 'tipo' | 'Area' | 'Team' | 'Position' | 'Position Information' | 'Lead' | 'Seniority' | 'seniority_nivel'>;

export function filtrar<T extends TaFiltrable>(rows: T[], f: TaFiltros = {}, cfg: TaMetricsConfig = DEFAULT_TA_CONFIG, universo: T[] = rows): T[] {
  const lista = (v: TaFiltroTexto | undefined) => (Array.isArray(v) ? v : v ? [v] : []).filter(Boolean);
  const nivelDe = f.Seniority ? seniorityResolver(cfg) : null;
  // Por valor pedido: su nivel canónico (null si es ambiguo o desconocido → comparación literal)
  const seniorities = lista(f.Seniority).map((v) => ({ v, nivel: nivelDe ? nivelDe(v) : null }));
  // El término se resuelve sobre `universo` (la base completa), no sobre `rows` ya
  // recortado por período: así el campo/valores que se declaran en el Criterio son
  // los que realmente filtraron (code review PR V1.3).
  const porTermino = f.termino ? new Set<T>(resolverTermino(universo, f.termino, cfg).filas) : null;
  return rows.filter((r) => {
    if (f.estado) {
      const wanted = Array.isArray(f.estado) ? f.estado : [f.estado];
      if (!wanted.some((e) => eq(e, r.estado))) return false;
    }
    for (const k of CAMPOS_FILTRO_TEXTO) {
      const vs = lista(f[k]);
      if (!vs.length) continue;
      if (k === 'Seniority') {
        // Por nivel canónico (§14.2): "senior analyst" = Sr Analyst + Senior + Semi Senior; "manager" ≠ "Sr Manager".
        const ok = seniorities.some(({ v, nivel }) => (nivel === null
          ? same(r.Seniority, v)
          : r.seniority_nivel !== null && canon(r.seniority_nivel) === canon(nivel)));
        if (!ok) return false;
        continue;
      }
      if (!vs.some((v) => same(String(r[k] ?? ''), v))) return false;
    }
    if (porTermino && !porTermino.has(r)) return false;
    return true;
  });
}

/**
 * Cuando un filtro de texto no encuentra nada: los valores que SÍ existen en el
 * universo y se parecen al pedido (comparten algún token canónico, o uno
 * contiene al otro), con su cantidad. Para que el bot reintente con el valor
 * real en vez de decir "no existe".
 */
export function valoresCercanos(rows: TaRow[], campo: (typeof CAMPOS_FILTRO_TEXTO)[number], pedido: string, limite = 8): Array<{ valor: string; n: number }> {
  const want = canon(pedido);
  const wantTokens = new Set(want.split(' ').filter(Boolean));
  const counts = new Map<string, number>();
  for (const r of rows) {
    const v = String(r[campo] ?? '').trim();
    if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  const scored = [...counts.entries()].map(([valor, n]) => {
    const c = canon(valor);
    const tokens = c.split(' ').filter(Boolean);
    const shared = tokens.filter((t) => wantTokens.has(t)).length;
    const contains = c.includes(want) || want.includes(c) ? 1 : 0;
    return { valor, n, score: shared * 2 + contains };
  });
  return scored
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || b.n - a.n || a.valor.localeCompare(b.valor))
    .slice(0, limite)
    .map(({ valor, n }) => ({ valor, n }));
}

/** Filtro con los diccionarios de la config + ID puntual (lo usan las herramientas del bot y la vista Hiring Plan). */
export function aplicarFiltros<T extends TaFiltrable & { ID: string }>(rows: T[], f: TaFiltros & { ID?: string }, cfg: TaMetricsConfig = DEFAULT_TA_CONFIG, universo: T[] = rows): T[] {
  const { ID, ...resto } = f;
  const base = ID ? rows.filter((r) => r.ID === String(ID).trim()) : rows;
  return filtrar(base, resto, cfg, universo);
}

/** Candidatos agrupados por etapa, de la más avanzada a la menos: { "5 Bar Raiser": ["Ana", "Juan"] }. */
export function embudoDe(r: { candidatos: TaCandidato[] }, conOrigen = false): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const etapa of Object.keys(ETAPAS_CANDIDATOS).sort((a, b) => ETAPAS_CANDIDATOS[b] - ETAPAS_CANDIDATOS[a])) {
    const nombres = r.candidatos.filter((c) => c.etapa === etapa).map((c) => (conOrigen ? `${c.nombre} (${c.origen})` : c.nombre));
    if (nombres.length) out[etapa] = nombres;
  }
  return out;
}

/** Nivel (2–7) del candidato más avanzado; 0 sin candidatos. Para ordenar el seguimiento (§17.3). */
export function nivelMasAvanzado(r: { candidatos: TaCandidato[] }): number {
  return r.candidatos.reduce((m, c) => Math.max(m, c.nivel), 0);
}

/** Cartera actual: estado Abierta (no suma Request, Pipeline, Programada, Stand By ni Cancelada). */
export function abiertas(rows: TaRow[]): TaRow[] {
  return rows.filter((r) => r.estado === 'Abierta');
}

export interface CorteFila { valor: string; n: number }

/** Corte ordenado de mayor a menor + Total (la fila 'Total' suma el universo). */
export function porDimension(rows: TaRow[], dim: TaDimension): { filas: CorteFila[]; total: number } {
  const m = new Map<string, number>();
  for (const r of rows) {
    const v = String(r[dim] ?? '').trim() || 'Sin dato';
    m.set(v, (m.get(v) ?? 0) + 1);
  }
  const filas = [...m.entries()].map(([valor, n]) => ({ valor, n })).sort((a, b) => b.n - a.n || a.valor.localeCompare(b.valor));
  return { filas, total: rows.length };
}

export function mediana(values: number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

export function promedio(values: number[]): number | null {
  if (!values.length) return null;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}

export interface AgeingResumen {
  N: number;
  excluidas_sin_fecha: number;
  promedio: number | null;
  mediana: number | null;
  mas_de_30: number;
  mas_de_60: number;
  maximo: number | null;
}

/** Días abiertas (hoy − fecha_apertura), solo estado Abierta. Excluye e informa las sin fecha. */
export function ageing(rows: TaRow[], hoy: string): AgeingResumen {
  const ab = abiertas(rows);
  const dias: number[] = [];
  let sinFecha = 0;
  for (const r of ab) {
    if (!r.fecha_apertura) { sinFecha++; continue; }
    dias.push(diffDays(r.fecha_apertura, hoy));
  }
  return {
    N: dias.length,
    excluidas_sin_fecha: sinFecha,
    promedio: promedio(dias),
    mediana: mediana(dias),
    mas_de_30: dias.filter((d) => d > 30).length,
    mas_de_60: dias.filter((d) => d > 60).length,
    maximo: dias.length ? Math.max(...dias) : null,
  };
}

/** Cerradas con Hire date dentro del período (ambos extremos incluidos). */
export function cierresPeriodo(rows: TaRow[], desde: string, hasta: string): TaRow[] {
  return rows.filter((r) => r.estado === 'Cerrada' && !!r.fecha_cierre && r.fecha_cierre >= desde && r.fecha_cierre <= hasta);
}

/** Todas las filas 8 Covered, incluso sin Hire date. */
export function cierresHistoricos(rows: TaRow[]): TaRow[] {
  return rows.filter((r) => r.estado === 'Cerrada');
}

export interface TiempoCierreResumen {
  cierres: number;
  N: number;
  promedio_dias: number | null;
  mediana_dias: number | null;
  excluidas_sin_fechas: number;
  negativos: number;
  ceros: number;
}

/** N, promedio y mediana de dias_cierre + exclusiones y negativos, para el Criterio. */
export function tiempoCierre(cierres: TaRow[]): TiempoCierreResumen {
  const s = cierres.map((r) => r.dias_cierre).filter((d): d is number => d !== null);
  return {
    cierres: cierres.length,
    N: s.length,
    promedio_dias: promedio(s),
    mediana_dias: mediana(s),
    excluidas_sin_fechas: cierres.length - s.length,
    negativos: s.filter((d) => d < 0).length,
    ceros: s.filter((d) => d === 0).length,
  };
}

/** Búsquedas iniciadas en el período, por fecha_apertura. */
export function creadasPeriodo(rows: TaRow[], desde: string, hasta: string): TaRow[] {
  return rows.filter((r) => !!r.fecha_apertura && r.fecha_apertura >= desde && r.fecha_apertura <= hasta);
}

/** Proxy histórico (§5.4): apertura ≤ X y Hire date vacía o > X. No se excluye nada más (§11.5). */
export function proxyAbiertasEn(rows: TaRow[], fechaX: string): { proxy: number; hoy_por_estado: Record<string, number>; sin_fecha_apertura: number } {
  const p = rows.filter((r) => !!r.fecha_apertura && r.fecha_apertura <= fechaX && (!r.fecha_cierre || r.fecha_cierre > fechaX));
  const porEstado: Record<string, number> = {};
  for (const r of p) porEstado[r.estado] = (porEstado[r.estado] ?? 0) + 1;
  return { proxy: p.length, hoy_por_estado: porEstado, sin_fecha_apertura: rows.filter((r) => !r.fecha_apertura).length };
}

/** Request, Pipeline, Programada, Stand By y Cancelada: se informan por separado. */
export function estadosNoAbiertos(rows: TaRow[]): TaRow[] {
  return rows.filter((r) => ESTADOS_NO_ABIERTOS.includes(r.estado));
}

export interface ChequeosCalidad {
  filas: number;
  sin_fecha_apertura: number;
  cerradas_sin_hire_date: number;
  cierres_negativos: number;
  canceladas_con_hire_date: number;
  filas_con_id_duplicado: number;
  sin_tipo_definido: number;
  estado_otro: number;
}

/** Chequeos de la sección 10, cuantificados. */
export function chequeosCalidad(rows: TaRow[]): ChequeosCalidad {
  const c = cierresHistoricos(rows);
  const ids = new Map<string, number>();
  for (const r of rows) ids.set(r.ID, (ids.get(r.ID) ?? 0) + 1);
  return {
    filas: rows.length,
    sin_fecha_apertura: rows.filter((r) => !r.fecha_apertura).length,
    cerradas_sin_hire_date: c.filter((r) => !r.fecha_cierre).length,
    cierres_negativos: c.filter((r) => r.dias_cierre !== null && r.dias_cierre < 0).length,
    canceladas_con_hire_date: rows.filter((r) => r.estado === 'Cancelada' && !!r.fecha_cierre).length,
    filas_con_id_duplicado: rows.filter((r) => (ids.get(r.ID) ?? 0) > 1).length,
    sin_tipo_definido: rows.filter((r) => r.tipo === 'Sin definir').length,
    estado_otro: rows.filter((r) => r.estado === 'Otro').length,
  };
}

// ─── Períodos (§5.5) ─────────────────────────────────────────────────────────

export interface Periodo { desde: string; hasta: string; label: string }

export function trimestre(year: number, q: 1 | 2 | 3 | 4, hoy?: string): Periodo {
  const startMonth = (q - 1) * 3 + 1;
  const endMonth = startMonth + 2;
  const lastDay = new Date(Date.UTC(year, endMonth, 0)).getUTCDate();
  const desde = `${year}-${String(startMonth).padStart(2, '0')}-01`;
  let hasta = `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  // Trimestre en curso: hasta hoy
  if (hoy && hoy >= desde && hoy < hasta) hasta = hoy;
  return { desde, hasta, label: `Q${q} ${year}` };
}

/** YTD = 1 de enero a hoy; `yearsBack` da el mismo período del año anterior (comparación interanual). */
export function ytd(hoy: string, yearsBack = 0): Periodo {
  const y = Number(hoy.slice(0, 4)) - yearsBack;
  const hasta = `${y}${hoy.slice(4)}`;
  return { desde: `${y}-01-01`, hasta, label: yearsBack ? `01/01–${hasta.slice(8, 10)}/${hasta.slice(5, 7)} ${y}` : `YTD ${y}` };
}

export function mes(year: number, month: number, hoy?: string): Periodo {
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const desde = `${year}-${String(month).padStart(2, '0')}-01`;
  let hasta = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  if (hoy && hoy >= desde && hoy < hasta) hasta = hoy;
  return { desde, hasta, label: `${year}-${String(month).padStart(2, '0')}` };
}

/** Últimos N meses (incluido el actual) como períodos, del más viejo al más nuevo. */
export function ultimosMeses(hoy: string, n: number): Periodo[] {
  const y = Number(hoy.slice(0, 4));
  const m = Number(hoy.slice(5, 7));
  const out: Periodo[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push(mes(d.getUTCFullYear(), d.getUTCMonth() + 1, hoy));
  }
  return out;
}

export function trimestreDe(fecha: string): 1 | 2 | 3 | 4 {
  return (Math.floor((Number(fecha.slice(5, 7)) - 1) / 3) + 1) as 1 | 2 | 3 | 4;
}

// ─── Histórico de estados: fotos semanales y diff (doc v5 §14, RF-23 / RF-25) ─

/**
 * Lo que se guarda de cada búsqueda en una foto. La Hiring Plan sobrescribe
 * estado y candidatos: sin esto no hay forma de saber qué se movió desde la
 * semana pasada. Append-only: cada foto suma filas, nada se edita ni se borra.
 */
export interface TaSnapshotRow extends TaFiltrable {
  ID: string;
  posicion: string;
  OL: string;
  fecha_apertura: string | null;
  fecha_cierre: string | null;
  candidatos: TaCandidato[];
  contactados: number | null;
  contratado: string;
}

export function snapshotDe(r: TaRow): TaSnapshotRow {
  return {
    ID: r.ID,
    posicion: r['Position Information'] || r.Position,
    Position: r.Position, 'Position Information': r['Position Information'],
    Area: r.Area, Team: r.Team, recruiter: r.recruiter, Lead: r.Lead, pais: r.pais, tipo: r.tipo,
    Seniority: r.Seniority, seniority_nivel: r.seniority_nivel,
    estado: r.estado, OL: r.OL,
    fecha_apertura: r.fecha_apertura, fecha_cierre: r.fecha_cierre,
    candidatos: r.candidatos, contactados: r.contactados, contratado: r.contratado,
  };
}

/** Días de gracia con que una búsqueda Cerrada sigue entrando en la foto (para que el cierre se vea en la comparación). */
export const SNAPSHOT_DIAS_CERRADA = 30;

/**
 * Qué entra en la foto: todo lo que NO está Cerrada, más las Cerradas de los
 * últimos 30 días (si solo guardáramos abiertas, un cierre se vería como
 * "desapareció de la base" en vez de "Cerrada · contratado X").
 */
export function entraEnFoto(r: Pick<TaSnapshotRow, 'estado' | 'fecha_cierre'>, hoy: string): boolean {
  if (r.estado !== 'Cerrada') return true;
  return !!r.fecha_cierre && r.fecha_cierre <= hoy && diffDays(r.fecha_cierre, hoy) <= SNAPSHOT_DIAS_CERRADA;
}

/**
 * Clave de una fila dentro de una foto. El ID solo, salvo que la planilla lo
 * repita (pasa: 648, 649, 650, 653, 729): ahí es ID#posición (canónica), que
 * es estable entre fotos aunque cambie el orden de las filas — con un sufijo
 * posicional, reordenar la Hiring Plan cruzaba estado y candidatos de dos
 * búsquedas distintas (code review). Si ID y posición coinciden queda ~n.
 */
export function claveSnapshot(rows: TaSnapshotRow[]): Map<TaSnapshotRow, string> {
  const porId = new Map<string, number>();
  for (const r of rows) porId.set(r.ID, (porId.get(r.ID) ?? 0) + 1);
  const usadas = new Map<string, number>();
  const out = new Map<TaSnapshotRow, string>();
  for (const r of rows) {
    const base = (porId.get(r.ID) ?? 0) > 1 ? idPosicion(r) : r.ID;
    const n = usadas.get(base) ?? 0;
    usadas.set(base, n + 1);
    out.set(r, n === 0 ? base : `${base}~${n + 1}`);
  }
  return out;
}

export interface TaCandidatoMovimiento {
  ID: string;
  posicion: string;
  nombre: string;
  /** Etapa anterior (null si entró) y actual (null si salió). */
  de: string | null;
  a: string | null;
  tipo: 'entro' | 'avanzo' | 'retrocedio' | 'salio' | 'contratado';
}

export interface TaSnapshotDiff {
  desde: string;
  hasta: string;
  /** Búsquedas que no estaban en la foto anterior. */
  nuevas: Array<{ ID: string; posicion: string; estado: TaEstado; OL: string }>;
  /** Búsquedas de la foto anterior que ya no están, o que pasaron a Cerrada/Cancelada. */
  cerradas: Array<{ ID: string; posicion: string; estado_anterior: TaEstado; estado_actual: TaEstado | 'fuera de la base'; contratado?: string }>;
  /** Cambios de estado u OL en búsquedas presentes en ambas fotos. */
  cambios: Array<{ ID: string; posicion: string; de: string; a: string }>;
  movimientos: TaCandidatoMovimiento[];
  resumen: {
    busquedas_antes: number; busquedas_ahora: number; nuevas: number; cerradas: number;
    entraron: number; avanzaron: number; retrocedieron: number; salieron: number; contratados: number;
  };
}

/** ID + posición canónica: lo que identifica una búsqueda cuando la planilla repite el ID. */
export const idPosicion = (r: Pick<TaSnapshotRow, 'ID' | 'posicion'>) => `${r.ID}#${foldKey(r.posicion) || 'sin-posicion'}`;

/**
 * Empareja las filas de dos fotos: por ID cuando el ID es único en AMBAS
 * (aunque la posición se haya editado), y por ID + posición cuando alguna de
 * las dos trae el ID repetido. No depende de cómo se guardó cada foto ni del
 * orden de las filas: comparar una foto contra la base viva (donde un ID único
 * en la foto puede estar repetido) no inventa "nuevas" ni "fuera de la base".
 * Devuelve [prev, curr] con null del lado que falta.
 */
export function emparejarSnapshots(prev: TaSnapshotRow[], curr: TaSnapshotRow[]): Array<[TaSnapshotRow | null, TaSnapshotRow | null]> {
  const porId = (rows: TaSnapshotRow[]) => {
    const m = new Map<string, TaSnapshotRow[]>();
    for (const r of rows) m.set(r.ID, [...(m.get(r.ID) ?? []), r]);
    return m;
  };
  const gp = porId(prev), gc = porId(curr);
  const out: Array<[TaSnapshotRow | null, TaSnapshotRow | null]> = [];
  for (const id of new Set([...gp.keys(), ...gc.keys()])) {
    const P = gp.get(id) ?? [], C = gc.get(id) ?? [];
    if (P.length <= 1 && C.length <= 1) { out.push([P[0] ?? null, C[0] ?? null]); continue; }
    const libresC = [...C];
    for (const p of P) {
      // Misma posición; si hay varias iguales (la planilla repite ID Y posición), la de la misma fecha de apertura
      let i = libresC.findIndex((c) => idPosicion(c) === idPosicion(p) && c.fecha_apertura === p.fecha_apertura);
      if (i < 0) i = libresC.findIndex((c) => idPosicion(c) === idPosicion(p));
      out.push([p, i >= 0 ? libresC.splice(i, 1)[0] : null]);
    }
    for (const c of libresC) out.push([null, c]);
  }
  return out;
}

const rotulo = (r: TaSnapshotRow) => `${r.estado} · ${r.OL}`;

/**
 * Qué se movió entre dos fotos (o entre una foto y la base de hoy). Puro.
 * Candidatos: por nombre (forma canónica) dentro de cada búsqueda; "salió"
 * cubre tanto descarte como que dejó de cargarse; "contratado" si figura en
 * Hired ahora y no antes.
 */
export function diffSnapshots(prev: TaSnapshotRow[], curr: TaSnapshotRow[], desde: string, hasta: string): TaSnapshotDiff {
  const nuevas: TaSnapshotDiff['nuevas'] = [];
  const cerradas: TaSnapshotDiff['cerradas'] = [];
  const cambios: TaSnapshotDiff['cambios'] = [];
  const movimientos: TaCandidatoMovimiento[] = [];

  for (const [p, c] of emparejarSnapshots(prev, curr)) {
    if (!c) { cerradas.push({ ID: p!.ID, posicion: p!.posicion, estado_anterior: p!.estado, estado_actual: 'fuera de la base' }); continue; }
    if (!p) { nuevas.push({ ID: c.ID, posicion: c.posicion, estado: c.estado, OL: c.OL }); continue; }
    if (rotulo(p) !== rotulo(c)) {
      cambios.push({ ID: c.ID, posicion: c.posicion, de: rotulo(p), a: rotulo(c) });
      // Presente en ambas pero pasó a Cerrada/Cancelada: también es un cierre para el informe
      if ((c.estado === 'Cerrada' || c.estado === 'Cancelada') && p.estado !== c.estado) {
        cerradas.push({ ID: c.ID, posicion: c.posicion, estado_anterior: p.estado, estado_actual: c.estado, ...(c.contratado ? { contratado: c.contratado } : {}) });
      }
    }
    // candidatos
    const antes = new Map(p.candidatos.map((x) => [foldKey(x.nombre), x]));
    const ahora = new Map(c.candidatos.map((x) => [foldKey(x.nombre), x]));
    for (const [n, x] of ahora) {
      const y = antes.get(n);
      if (!y) movimientos.push({ ID: c.ID, posicion: c.posicion, nombre: x.nombre, de: null, a: x.etapa, tipo: 'entro' });
      else if (x.nivel > y.nivel) movimientos.push({ ID: c.ID, posicion: c.posicion, nombre: x.nombre, de: y.etapa, a: x.etapa, tipo: 'avanzo' });
      else if (x.nivel < y.nivel) movimientos.push({ ID: c.ID, posicion: c.posicion, nombre: x.nombre, de: y.etapa, a: x.etapa, tipo: 'retrocedio' });
    }
    const nuevoContratado = !!c.contratado && foldKey(p.contratado || '') !== foldKey(c.contratado);
    for (const [n, y] of antes) {
      if (ahora.has(n)) continue;
      const contratado = nuevoContratado && foldKey(c.contratado) === n;
      movimientos.push({ ID: c.ID, posicion: c.posicion, nombre: y.nombre, de: y.etapa, a: contratado ? 'Hired' : null, tipo: contratado ? 'contratado' : 'salio' });
    }
    if (nuevoContratado && !movimientos.some((m) => m.ID === c.ID && m.tipo === 'contratado')) {
      movimientos.push({ ID: c.ID, posicion: c.posicion, nombre: c.contratado, de: null, a: 'Hired', tipo: 'contratado' });
    }
  }
  const cuenta = (t: TaCandidatoMovimiento['tipo']) => movimientos.filter((m) => m.tipo === t).length;
  return {
    desde, hasta, nuevas, cerradas, cambios, movimientos,
    resumen: {
      busquedas_antes: prev.length, busquedas_ahora: curr.length, nuevas: nuevas.length, cerradas: cerradas.length,
      entraron: cuenta('entro'), avanzaron: cuenta('avanzo'), retrocedieron: cuenta('retrocedio'), salieron: cuenta('salio'), contratados: cuenta('contratado'),
    },
  };
}
